import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api, ApiError, session } from './client';

/** Backend paginated envelope: {items, total, page, page_size}. */
interface BackendPage<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

function toPaginatedResponse<T>(d: BackendPage<T>): PaginatedResponse<T> {
  return {
    data: d.items,
    pagination: {
      page: d.page,
      page_size: d.page_size,
      total: d.total,
      total_pages: d.page_size > 0 ? Math.ceil(d.total / d.page_size) : 0,
    },
  };
}
import type {
  User,
  Organization,
  Client,
  ClientWithStats,
  ClientHealth,
  Workspace,
  LoginRequest,
  LoginResponse,
  SignupRequest,
  HealthStatus,
  Team,
  TeamMember,
  CreateTeamRequest,
  UpdateTeamRequest,
  AddTeamMemberRequest,
  CreateUserRequest,
  UpdateUserRequest,
  InviteUserRequest,
  CreateClientRequest,
  UpdateClientRequest,
  AdAccount,
  AdPlatform,
  ConnectAdAccountRequest,
  UpdateAdAccountRequest,
  DataSource,
  CreateDataSourceRequest,
  Role,
  CreateRoleRequest,
  UpdateRoleRequest,
  AuditLogEntry,
  AuditLogFilters,
  PaginationParams,
  PaginatedResponse,
} from './types';

// Query keys
export const queryKeys = {
  // Auth
  currentUser: ['auth', 'me'] as const,

  // Organizations
  organizations: ['organizations'] as const,
  organization: (id: string) => ['organizations', id] as const,

  // Teams
  teams: (orgId: string) => ['teams', orgId] as const,
  team: (id: string) => ['teams', 'detail', id] as const,
  teamMembers: (teamId: string) => ['teams', teamId, 'members'] as const,

  // Users
  users: (orgId: string, params?: PaginationParams) => ['users', orgId, params] as const,
  user: (id: string) => ['users', 'detail', id] as const,

  // Roles
  roles: ['roles'] as const,
  role: (id: string) => ['roles', id] as const,

  // Clients
  clients: (orgId: string, params?: PaginationParams) => ['clients', orgId, params] as const,
  client: (id: string) => ['clients', 'detail', id] as const,
  clientHealth: (id: string) => ['clients', id, 'health'] as const,
  clientTeams: (clientId: string) => ['clients', clientId, 'teams'] as const,
  clientUsers: (clientId: string) => ['clients', clientId, 'users'] as const,

  // Workspaces
  workspaces: (clientId: string) => ['workspaces', clientId] as const,
  workspace: (id: string) => ['workspaces', 'detail', id] as const,

  // Ad Accounts
  adAccounts: (clientId: string) => ['ad-accounts', clientId] as const,
  adAccount: (id: string) => ['ad-accounts', 'detail', id] as const,

  // Data Sources
  dataSources: (clientId: string) => ['data-sources', clientId] as const,
  dataSource: (id: string) => ['data-sources', 'detail', id] as const,

  // Audit Log
  auditLog: (orgId: string, filters?: AuditLogFilters) => ['audit-log', orgId, filters] as const,

  // Health
  health: ['health'] as const,
};

// Auth hooks
export function useCurrentUser() {
  return useQuery({
    queryKey: queryKeys.currentUser,
    queryFn: async () => {
      // /auth/me returns SessionInfo {session_id, user, expires_at, ...} —
      // unwrap to the flat User so user.role matches the /auth/login shape.
      // Without this, role is undefined on every reload/direct-URL, and every
      // permission-gated page renders "Access Denied" (release-blocker).
      const response = await api.get<{ user: User } | User>('/auth/me');
      const data = response.data as { user?: User };
      return (data.user ?? response.data) as User;
    },
    // Don't probe /auth/me before a session exists — a pre-login 401 is
    // not an error state, just "logged out" (see the interceptor note).
    enabled: !!session.getToken(),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation<LoginResponse, ApiError, LoginRequest>({
    mutationFn: async (credentials) => {
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.currentUser, data.user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError>({
    mutationFn: async () => {
      await api.post('/auth/logout');
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

export function useSignup() {
  return useMutation<LoginResponse, ApiError, SignupRequest>({
    mutationFn: async (data) => {
      const response = await api.post<LoginResponse>('/auth/register', data);
      return response.data;
    },
  });
}

// Organization hooks
export function useOrganizations() {
  return useQuery({
    queryKey: queryKeys.organizations,
    queryFn: async () => {
      const response = await api.get<Organization[]>('/organizations');
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useOrganization(id: string | null) {
  return useQuery({
    queryKey: queryKeys.organization(id!),
    queryFn: async () => {
      const response = await api.get<Organization>(`/organizations/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export interface OrganizationStats {
  organization_id: number;
  team_count: number;
  client_count: number;
  user_count: number;
  workspace_count: number;
}

export function useOrganizationStats(id: string | null) {
  return useQuery({
    queryKey: ['organizations', id, 'stats'],
    queryFn: async () => {
      const response = await api.get<OrganizationStats>(`/organizations/${id}/stats`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

// Client hooks
export function useClients(organizationId: string | null) {
  return useQuery({
    queryKey: queryKeys.clients(organizationId!),
    queryFn: async () => {
      const response = await api.get<Client[]>(`/clients?organization_id=${organizationId}`);
      return response.data;
    },
    enabled: !!organizationId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useClient(id: string | null) {
  return useQuery({
    queryKey: queryKeys.client(id!),
    queryFn: async () => {
      const response = await api.get<Client>(`/clients/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

// Workspace hooks
export function useWorkspaces(clientId: string | null) {
  return useQuery({
    queryKey: queryKeys.workspaces(clientId!),
    queryFn: async () => {
      // Backend returns an envelope {total, items}
      const response = await api.get<Workspace[] | { total: number; items: Workspace[] }>(
        `/workspaces?client_id=${clientId}`
      );
      const d = response.data;
      return Array.isArray(d) ? d : d.items;
    },
    enabled: !!clientId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useWorkspace(id: string | null) {
  return useQuery({
    queryKey: queryKeys.workspace(id!),
    queryFn: async () => {
      const response = await api.get<Workspace>(`/workspaces/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export interface CreateWorkspaceRequest {
  name: string;
  slug: string;
  client_id?: number;
  plan?: string;
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation<Workspace, ApiError, CreateWorkspaceRequest>({
    mutationFn: async (data) => {
      const response = await api.post<Workspace>('/workspaces', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      if (variables.client_id != null) {
        queryClient.invalidateQueries({ queryKey: queryKeys.workspaces(String(variables.client_id)) });
      }
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
}

// Health hooks
export function useHealth() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: async () => {
      const response = await api.get<HealthStatus>('/health');
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // 1 minute
  });
}

// ==========================================
// Team hooks
// ==========================================

export function useTeams(organizationId: string | null) {
  return useQuery({
    queryKey: queryKeys.teams(organizationId!),
    queryFn: async () => {
      const response = await api.get<Team[]>(`/teams?organization_id=${organizationId}`);
      return response.data;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTeam(id: string | null) {
  return useQuery({
    queryKey: queryKeys.team(id!),
    queryFn: async () => {
      const response = await api.get<Team>(`/teams/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useTeamMembers(teamId: string | null) {
  return useQuery({
    queryKey: queryKeys.teamMembers(teamId!),
    queryFn: async () => {
      const response = await api.get<TeamMember[]>(`/teams/${teamId}/members`);
      return response.data;
    },
    enabled: !!teamId,
  });
}

export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation<Team, ApiError, CreateTeamRequest & { organization_id: string }>({
    mutationFn: async (data) => {
      const response = await api.post<Team>('/teams', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams(variables.organization_id) });
    },
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation<Team, ApiError, { id: string; data: UpdateTeamRequest }>({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<Team>(`/teams/${id}`, data);
      return response.data;
    },
    onSuccess: (team) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team(team.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams(team.organization_id) });
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: string; organization_id: string }>({
    mutationFn: async ({ id }) => {
      await api.delete(`/teams/${id}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams(variables.organization_id) });
    },
  });
}

export function useAddTeamMember() {
  const queryClient = useQueryClient();

  return useMutation<TeamMember, ApiError, { teamId: string; data: AddTeamMemberRequest }>({
    mutationFn: async ({ teamId, data }) => {
      const response = await api.post<TeamMember>(`/teams/${teamId}/members`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teamMembers(variables.teamId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.team(variables.teamId) });
    },
  });
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { teamId: string; userId: string }>({
    mutationFn: async ({ teamId, userId }) => {
      await api.delete(`/teams/${teamId}/members/${userId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teamMembers(variables.teamId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.team(variables.teamId) });
    },
  });
}

// ==========================================
// User hooks (extended)
// ==========================================

export function useUsers(organizationId: string | null, params?: PaginationParams) {
  return useQuery({
    queryKey: queryKeys.users(organizationId!, params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('organization_id', organizationId!);
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.page_size) searchParams.set('page_size', params.page_size.toString());
      if (params?.sort_by) searchParams.set('sort_by', params.sort_by);
      if (params?.sort_direction) searchParams.set('sort_direction', params.sort_direction);

      const response = await api.get<BackendPage<User & { full_name?: string | null }>>(
        `/users?${searchParams}`
      );
      // The API returns full_name (nullable); the UI reads `name`. Normalize
      // so consumers get a non-null display name and never crash on null
      // (e.g. an avatar initial from name[0]).
      const normalized = {
        ...response.data,
        items: response.data.items.map((u) => ({
          ...u,
          name: u.name ?? u.full_name ?? u.email,
        })),
      };
      return toPaginatedResponse(normalized);
    },
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUser(id: string | null) {
  return useQuery({
    queryKey: queryKeys.user(id!),
    queryFn: async () => {
      const response = await api.get<User>(`/users/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation<User, ApiError, CreateUserRequest & { organization_id: string }>({
    mutationFn: async (data) => {
      const response = await api.post<User>('/users', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.organization_id] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation<User, ApiError, { id: string; data: UpdateUserRequest }>({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<User>(`/users/${id}`, data);
      return response.data;
    },
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user(user.id) });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, InviteUserRequest & { organization_id: string }>({
    mutationFn: async (data) => {
      await api.post('/users/invite', data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.organization_id] });
    },
  });
}

// ==========================================
// Role hooks
// ==========================================

export function useRoles() {
  return useQuery({
    queryKey: queryKeys.roles,
    queryFn: async () => {
      // Backend returns a paginated envelope {items, total, ...}
      const response = await api.get<Role[] | { items: Role[] }>('/roles');
      const d = response.data;
      return Array.isArray(d) ? d : d.items;
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function useRole(id: string | null) {
  return useQuery({
    queryKey: queryKeys.role(id!),
    queryFn: async () => {
      const response = await api.get<Role>(`/roles/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation<Role, ApiError, CreateRoleRequest>({
    mutationFn: async (data) => {
      const response = await api.post<Role>('/roles', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation<Role, ApiError, { id: string; data: UpdateRoleRequest }>({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<Role>(`/roles/${id}`, data);
      return response.data;
    },
    onSuccess: (role) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.role(role.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.roles });
    },
  });
}

// ==========================================
// Client hooks (extended with mutations)
// ==========================================

export function useClientsPaginated(organizationId: string | null, params?: PaginationParams) {
  return useQuery({
    queryKey: queryKeys.clients(organizationId!, params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('organization_id', organizationId!);
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.page_size) searchParams.set('page_size', params.page_size.toString());
      if (params?.sort_by) searchParams.set('sort_by', params.sort_by);
      if (params?.sort_direction) searchParams.set('sort_direction', params.sort_direction);

      const response = await api.get<BackendPage<ClientWithStats>>(`/clients?${searchParams}`);
      return toPaginatedResponse(response.data);
    },
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
}

export function useClientHealth(clientId: string | null) {
  return useQuery({
    queryKey: queryKeys.clientHealth(clientId!),
    queryFn: async () => {
      const response = await api.get<ClientHealth>(`/clients/${clientId}/health`);
      return response.data;
    },
    enabled: !!clientId,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });
}

/** Flat row returned by GET /clients/{id}/teams (verified in production). */
export interface ClientTeamRow {
  id: number;
  name: string;
  team_type: string;
  member_count: number;
  access_level: string;
  granted_at: string;
}

export function useClientTeams(clientId: string | null) {
  return useQuery({
    queryKey: queryKeys.clientTeams(clientId!),
    queryFn: async () => {
      const response = await api.get<ClientTeamRow[]>(`/clients/${clientId}/teams`);
      return response.data;
    },
    enabled: !!clientId,
  });
}

/** Flat row returned by GET /clients/{id}/users (verified in production). */
export interface ClientUserRow {
  id: number;
  email: string;
  full_name: string | null;
  role: string;
  access_level: string;
  access_source: string; // "direct" | "team:<name>"
  granted_at: string;
}

export function useClientUsers(clientId: string | null) {
  return useQuery({
    queryKey: queryKeys.clientUsers(clientId!),
    queryFn: async () => {
      const response = await api.get<ClientUserRow[]>(`/clients/${clientId}/users`);
      return response.data;
    },
    enabled: !!clientId,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation<Client, ApiError, CreateClientRequest & { organization_id: string }>({
    mutationFn: async (data) => {
      const response = await api.post<Client>('/clients', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients', variables.organization_id] });
    },
  });
}

export function useArchiveClient() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: string }>({
    mutationFn: async ({ id }) => {
      await api.delete(`/clients/${id}`); // soft archive (status=archived)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.client(variables.id) });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation<Client, ApiError, { id: string; data: UpdateClientRequest }>({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<Client>(`/clients/${id}`, data);
      return response.data;
    },
    onSuccess: (client) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.client(client.id) });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useAssignTeamToClient() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { clientId: string; teamId: string; accessLevel?: 'full' | 'read' }>({
    mutationFn: async ({ clientId, teamId, accessLevel = 'full' }) => {
      await api.post(`/clients/${clientId}/teams`, { team_id: teamId, access_level: accessLevel });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clientTeams(variables.clientId) });
    },
  });
}

export function useRemoveTeamFromClient() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { clientId: string; teamId: string }>({
    mutationFn: async ({ clientId, teamId }) => {
      await api.delete(`/clients/${clientId}/teams/${teamId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clientTeams(variables.clientId) });
    },
  });
}

// ==========================================
// Ad Account hooks
// ==========================================

export function useAdAccounts(clientId: string | null) {
  return useQuery({
    queryKey: queryKeys.adAccounts(clientId!),
    queryFn: async () => {
      // Backend returns a paginated envelope {items,...}; the tab expects an
      // array. Unwrap so accounts.reduce/.filter don't crash on the object.
      const response = await api.get<{ items: AdAccount[] } | AdAccount[]>(
        `/clients/${clientId}/ad-accounts`
      );
      const d = response.data;
      return Array.isArray(d) ? d : d.items;
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdAccount(id: string | null) {
  return useQuery({
    queryKey: queryKeys.adAccount(id!),
    queryFn: async () => {
      const response = await api.get<AdAccount>(`/ad-accounts/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useConnectAdAccount() {
  const queryClient = useQueryClient();

  return useMutation<AdAccount[], ApiError, ConnectAdAccountRequest & { client_id: string }>({
    mutationFn: async (data) => {
      const response = await api.post<AdAccount[]>(`/clients/${data.client_id}/ad-accounts/connect`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adAccounts(variables.client_id) });
    },
  });
}

export function useUpdateAdAccount() {
  const queryClient = useQueryClient();

  return useMutation<AdAccount, ApiError, { id: string; clientId: string; data: UpdateAdAccountRequest }>({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<AdAccount>(`/ad-accounts/${id}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adAccount(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.adAccounts(variables.clientId) });
    },
  });
}

export function useDisconnectAdAccount() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: string; clientId: string }>({
    mutationFn: async ({ id }) => {
      await api.delete(`/ad-accounts/${id}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adAccounts(variables.clientId) });
    },
  });
}

export function useSyncAdAccount() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: string; clientId: string }>({
    mutationFn: async ({ id }) => {
      await api.post(`/ad-accounts/${id}/sync`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adAccount(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.adAccounts(variables.clientId) });
    },
  });
}

export function useAdPlatformOAuthUrl(platform: AdPlatform, clientId: string) {
  return useQuery({
    queryKey: ['ad-platforms', platform, 'oauth-url', clientId],
    queryFn: async () => {
      const response = await api.get<{ url: string }>(`/ad-platforms/${platform}/oauth-url?client_id=${clientId}`);
      return response.data.url;
    },
    enabled: false, // Manual trigger
  });
}

// ==========================================
// Data Source hooks
// ==========================================

/** Row returned by GET /clients/{id}/data-sources (envelope {items}, verified in production). */
export interface DataSourceRow {
  id: number;
  client_id: number;
  workspace_id: number | null;
  name: string;
  source_type: string;
  source_type_label: string;
  description: string | null;
  status: string;
  health_status: string;
  error_message: string | null;
  is_enabled: boolean;
  last_sync_at?: string | null;
  created_at?: string;
}

export function useDataSources(clientId: string | null) {
  return useQuery({
    queryKey: queryKeys.dataSources(clientId!),
    queryFn: async () => {
      const response = await api.get<{ items: DataSourceRow[] } | DataSourceRow[]>(
        `/clients/${clientId}/data-sources`
      );
      const d = response.data;
      return Array.isArray(d) ? d : d.items;
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDataSource(id: string | null) {
  return useQuery({
    queryKey: queryKeys.dataSource(id!),
    queryFn: async () => {
      const response = await api.get<DataSource>(`/data-sources/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateDataSource() {
  const queryClient = useQueryClient();

  return useMutation<DataSource, ApiError, CreateDataSourceRequest & { client_id: string }>({
    mutationFn: async (data) => {
      const response = await api.post<DataSource>(`/clients/${data.client_id}/data-sources`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dataSources(variables.client_id) });
    },
  });
}

export function useDeleteDataSource() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: string; clientId: string }>({
    mutationFn: async ({ id }) => {
      await api.delete(`/data-sources/${id}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dataSources(variables.clientId) });
    },
  });
}

export function useTestDataSource() {
  const queryClient = useQueryClient();

  return useMutation<{ success?: boolean; status?: string; message?: string }, ApiError,
                     { id: string | number; clientId: string }>({
    mutationFn: async ({ id }) => {
      const response = await api.post<{ success?: boolean; status?: string; message?: string }>(
        `/data-sources/${id}/test`
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dataSources(variables.clientId) });
    },
  });
}

// ==========================================
// Audit Log hooks
// ==========================================

export function useAuditLog(organizationId: string | null, filters?: AuditLogFilters, params?: PaginationParams) {
  return useQuery({
    queryKey: [...queryKeys.auditLog(organizationId!, filters), params] as const,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('organization_id', organizationId!);
      if (filters?.user_id) searchParams.set('user_id', filters.user_id);
      if (filters?.action) searchParams.set('action', filters.action);
      if (filters?.entity_type) searchParams.set('entity_type', filters.entity_type);
      if (filters?.entity_id) searchParams.set('entity_id', filters.entity_id);
      // Backend filters are since/until + limit/offset
      if (filters?.date_from) searchParams.set('since', filters.date_from);
      if (filters?.date_to) searchParams.set('until', filters.date_to);
      const page = params?.page ?? 1;
      const pageSize = params?.page_size ?? 25;
      searchParams.set('limit', pageSize.toString());
      searchParams.set('offset', ((page - 1) * pageSize).toString());

      const response = await api.get<{ total: number; limit: number; offset: number; items: AuditLogEntry[] }>(
        `/audit-log?${searchParams}`
      );
      const d = response.data;
      return toPaginatedResponse({
        items: d.items,
        total: d.total,
        page,
        page_size: pageSize,
      });
    },
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}

// ==========================================
// F2 W4: Audience engine hooks (workspace-scoped via X-Workspace-ID).
// Every query key includes the caller's active workspace so switching
// workspaces refetches instead of serving another workspace's cache.
// ==========================================

export interface SavedAudienceRow {
  id: number;
  name: string;
  description: string | null;
  version: number;
  status: 'active' | 'archived';
  last_matched_records: number | null;
  last_estimated_size: number | null;
  last_meets_min_cohort: boolean | null;
  last_estimated_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export function useSavedAudiences(workspaceKey: string | null, includeArchived = false) {
  return useQuery({
    queryKey: ['saved-audiences', workspaceKey, includeArchived],
    queryFn: async () => {
      const response = await api.get<{ total: number; items: SavedAudienceRow[] }>(
        `/saved-audiences?include_archived=${includeArchived}`
      );
      return response.data;
    },
    enabled: !!workspaceKey,
    staleTime: 60 * 1000,
  });
}

export function useCreateSavedAudience(workspaceKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation<SavedAudienceRow, ApiError, { name: string; description?: string }>({
    mutationFn: async (data) => {
      const response = await api.post<SavedAudienceRow>('/saved-audiences', {
        ...data,
        filter: {},
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-audiences', workspaceKey] });
    },
  });
}

export function useArchiveSavedAudience(workspaceKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: number }>({
    mutationFn: async ({ id }) => {
      await api.delete(`/saved-audiences/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-audiences', workspaceKey] });
    },
  });
}

export function useRefreshAudienceEstimate(workspaceKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation<SavedAudienceRow, ApiError, { id: number }>({
    mutationFn: async ({ id }) => {
      const response = await api.post<SavedAudienceRow>(`/saved-audiences/${id}/refresh-estimate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-audiences', workspaceKey] });
    },
  });
}

export interface AudienceExportResult {
  export_id?: number;
  destination?: string;
  record_count?: number;
  status?: string;
  message?: string;
}

export function useExportSavedAudience() {
  return useMutation<AudienceExportResult, ApiError,
                     { id: number; destination: string; fmt?: 'csv' | 'json' }>({
    mutationFn: async ({ id, destination, fmt = 'csv' }) => {
      const response = await api.post<AudienceExportResult>(
        `/saved-audiences/${id}/export`, { destination, fmt }
      );
      return response.data;
    },
  });
}

export interface ExportTemplateRow {
  destination: string;
  name: string;
  default_fmt: string;
  requires_partner_activation: boolean;
  description: string;
  min_estimated_reach: number;
}

export function useExportTemplates() {
  return useQuery({
    queryKey: ['export-templates'],
    queryFn: async () => {
      const response = await api.get<{ templates: ExportTemplateRow[] }>('/exports/templates');
      return response.data.templates;
    },
    staleTime: 60 * 60 * 1000,
  });
}

export interface AudienceRequestRow {
  id: string;
  provider_id: string;
  status: string;
  estimated_reach: number | null;
  cohort_size: number | null;
  estimated_cost_amount: number | null;
  estimated_cost_currency: string;
  data_quality_score: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export function useAudienceRequests(workspaceKey: string | null, status?: string,
                                    page = 1, pageSize = 10) {
  return useQuery({
    queryKey: ['audience-requests', workspaceKey, status, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      params.set('limit', String(pageSize));
      params.set('offset', String((page - 1) * pageSize));
      const response = await api.get<{ total: number; items: AudienceRequestRow[] }>(
        `/audience-requests?${params}`
      );
      return response.data;
    },
    enabled: !!workspaceKey,
    staleTime: 30 * 1000,
  });
}

export interface AudienceProviderRow {
  provider_id: string;
  display_name?: string;
  name?: string;
  status?: string;
}

export function useAudienceProviders() {
  return useQuery({
    queryKey: ['audience-providers'],
    queryFn: async () => {
      const response = await api.get<{ total: number; providers: AudienceProviderRow[] }>(
        '/audience-requests/providers'
      );
      return response.data.providers;
    },
    staleTime: 60 * 60 * 1000,
  });
}

// ==========================================
// F2 W5: Campaign engine hooks (workspace-scoped via X-Workspace-ID).
// ==========================================

export interface CampaignRow {
  id: number;
  saved_audience_id: number;
  audience_name: string | null;
  name: string;
  objective: string;
  status: 'draft' | 'active' | 'completed' | 'archived';
  template_key: string | null;
  budget_total: number | null;
  budget_currency: string;
  start_date: string | null;
  end_date: string | null;
  destinations: string[];
  notes: string | null;
  last_estimated_size: number | null;
  export_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface CampaignTemplateRow {
  key: string;
  name: string;
  objective: string;
  description: string;
  recommended_destinations: string[];
  recommended_flight_days: number;
}

/** Mirror of the backend STATUS_TRANSITIONS map (enforced server-side). */
export const CAMPAIGN_TRANSITIONS: Record<string, string[]> = {
  draft: ['active', 'archived'],
  active: ['completed', 'archived'],
  completed: ['archived'],
  archived: [],
};

export function useCampaigns(workspaceKey: string | null, includeArchived = false) {
  return useQuery({
    queryKey: ['campaigns', workspaceKey, includeArchived],
    queryFn: async () => {
      const response = await api.get<{ total: number; items: CampaignRow[] }>(
        `/campaigns?include_archived=${includeArchived}`
      );
      return response.data;
    },
    enabled: !!workspaceKey,
    staleTime: 60 * 1000,
  });
}

export function useCampaignTemplates() {
  return useQuery({
    queryKey: ['campaign-templates'],
    queryFn: async () => {
      const response = await api.get<{ templates?: CampaignTemplateRow[]; items?: CampaignTemplateRow[] }>(
        '/campaigns/templates'
      );
      return response.data.templates ?? response.data.items ?? [];
    },
    staleTime: 60 * 60 * 1000,
  });
}

export interface CreateCampaignRequest {
  name: string;
  saved_audience_id: number;
  objective?: string;
  template_key?: string;
  budget_total?: number;
  start_date?: string;
  end_date?: string;
  destinations?: string[];
  notes?: string;
}

export function useCreateCampaign(workspaceKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation<CampaignRow, ApiError, CreateCampaignRequest>({
    mutationFn: async (data) => {
      const response = await api.post<CampaignRow>('/campaigns', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', workspaceKey] });
    },
  });
}

export function useUpdateCampaign(workspaceKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation<CampaignRow, ApiError, { id: number; data: Record<string, unknown> }>({
    mutationFn: async ({ id, data }) => {
      const response = await api.put<CampaignRow>(`/campaigns/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', workspaceKey] });
    },
  });
}

export function useArchiveCampaign(workspaceKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: number }>({
    mutationFn: async ({ id }) => {
      await api.delete(`/campaigns/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', workspaceKey] });
    },
  });
}

export function useExportCampaign(workspaceKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation<AudienceExportResult, ApiError, { id: number; destination: string }>({
    mutationFn: async ({ id, destination }) => {
      const response = await api.post<AudienceExportResult>(`/campaigns/${id}/export`, { destination });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', workspaceKey] });
      queryClient.invalidateQueries({ queryKey: ['campaign-exports', variables.id] });
    },
  });
}

export interface CampaignExportEntry {
  export_id?: number;
  id?: number;
  destination: string;
  fmt?: string;
  record_count?: number;
  created_at?: string;
}

export function useCampaignExports(campaignId: number | null) {
  return useQuery({
    queryKey: ['campaign-exports', campaignId],
    queryFn: async () => {
      const response = await api.get<{ items?: CampaignExportEntry[]; exports?: CampaignExportEntry[]; total?: number }>(
        `/campaigns/${campaignId}/exports`
      );
      return response.data.items ?? response.data.exports ?? [];
    },
    enabled: campaignId != null,
  });
}

// ==========================================
// F2 W6: Analytics/tracking hooks (workspace-scoped via X-Workspace-ID).
// ==========================================

export interface AnalyticsSummary {
  active_workspaces: number;
  total_events: number;
  active_users: number;
  sessions: number;
  conversions: number;
  revenue: number;
  average_order_value: number;
  period_start: string;
  period_end: string;
}

export interface FunnelStageRow {
  stage: string;
  sessions: number;
  events: number;
  conversion_rate: number;
  drop_off: number;
}

export interface DeviceBreakdownRow {
  device_category?: string;
  device?: string;
  users: number;
  events: number;
}

export interface AudienceGrowthRow {
  date: string;
  new_users: number;
}

export function useAnalyticsSummary(workspaceKey: string | null, days: number) {
  return useQuery({
    queryKey: ['analytics-summary', workspaceKey, days],
    queryFn: async () => {
      const response = await api.get<AnalyticsSummary>(`/analytics/summary?days=${days}`);
      return response.data;
    },
    enabled: !!workspaceKey,
    staleTime: 60 * 1000,
  });
}

export function useAnalyticsFunnel(workspaceKey: string | null, days: number) {
  return useQuery({
    queryKey: ['analytics-funnel', workspaceKey, days],
    queryFn: async () => {
      const response = await api.get<FunnelStageRow[]>(`/analytics/funnel?days=${days}`);
      return response.data ?? [];
    },
    enabled: !!workspaceKey,
    staleTime: 60 * 1000,
  });
}

export function useDeviceBreakdown(workspaceKey: string | null, days: number) {
  return useQuery({
    queryKey: ['analytics-devices', workspaceKey, days],
    queryFn: async () => {
      const response = await api.get<DeviceBreakdownRow[] | null>(`/analytics/device-breakdown?days=${days}`);
      return response.data ?? [];
    },
    enabled: !!workspaceKey,
    staleTime: 60 * 1000,
  });
}

export function useAudienceGrowth(workspaceKey: string | null, days: number) {
  return useQuery({
    queryKey: ['analytics-growth', workspaceKey, days],
    queryFn: async () => {
      const response = await api.get<AudienceGrowthRow[]>(`/analytics/audience-growth?days=${days}`);
      return response.data ?? [];
    },
    enabled: !!workspaceKey,
    staleTime: 60 * 1000,
  });
}

// ==========================================
// F2 W7: Analytics provider connections (workspace-scoped via X-Workspace-ID).
// ==========================================

export interface ProviderConnectionRow {
  id: number; // normalized from the backend's connection_id
  provider_id: string;
  status: string;
  is_active?: boolean;
  last_sync_at?: string | null;
  last_sync_status?: string | null;
  created_at?: string;
}

export interface ConnectionsHealth {
  workspace_id: number;
  total_connections: number;
  healthy_connections: number;
  unhealthy_connections: number;
  connections: Array<{ connection_id?: number; id?: number; provider_id?: string;
                       status?: string; healthy?: boolean }>;
  checked_at: string;
}

export function useProviderConnections(workspaceKey: string | null) {
  return useQuery({
    queryKey: ['provider-connections', workspaceKey],
    queryFn: async () => {
      // Backend items carry connection_id; normalize to id for the UI
      const response = await api.get<{
        connections: Array<Omit<ProviderConnectionRow, 'id' | 'status'> &
                          { connection_id: number; is_active: boolean }>;
        total: number;
      }>('/analytics/connections');
      return {
        total: response.data.total,
        connections: response.data.connections.map((c) => ({
          ...c,
          id: c.connection_id,
          status: c.is_active ? 'active' : 'inactive',
        })) as ProviderConnectionRow[],
      };
    },
    enabled: !!workspaceKey,
    staleTime: 30 * 1000,
  });
}

export function useConnectionsHealth(workspaceKey: string | null) {
  return useQuery({
    queryKey: ['connections-health', workspaceKey],
    queryFn: async () => {
      const response = await api.get<ConnectionsHealth>('/analytics/health/connections');
      return response.data;
    },
    enabled: !!workspaceKey,
    staleTime: 30 * 1000,
  });
}

export function useCreateProviderConnection(workspaceKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation<ProviderConnectionRow, ApiError,
                     { provider_id: string; credentials: Record<string, unknown>;
                       config?: Record<string, unknown> }>({
    mutationFn: async (data) => {
      const response = await api.post<ProviderConnectionRow>('/analytics/connections', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-connections', workspaceKey] });
      queryClient.invalidateQueries({ queryKey: ['connections-health', workspaceKey] });
    },
  });
}

export function useTestProviderConnection(workspaceKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation<{ success?: boolean; status?: string; message?: string }, ApiError,
                     { id: number }>({
    mutationFn: async ({ id }) => {
      const response = await api.post<{ success?: boolean; status?: string; message?: string }>(
        `/analytics/connections/${id}/test`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections-health', workspaceKey] });
    },
  });
}

export function useDeleteProviderConnection(workspaceKey: string | null) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: number }>({
    mutationFn: async ({ id }) => {
      await api.delete(`/analytics/connections/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-connections', workspaceKey] });
      queryClient.invalidateQueries({ queryKey: ['connections-health', workspaceKey] });
    },
  });
}

export function useOAuthAuthorizeUrl() {
  return useMutation<{ auth_url: string; state?: string }, ApiError,
                     { providerId: string; redirectUri: string }>({
    mutationFn: async ({ providerId, redirectUri }) => {
      const response = await api.get<{ auth_url: string; state?: string }>(
        `/analytics/oauth/authorize/${providerId}?redirect_uri=${encodeURIComponent(redirectUri)}`
      );
      return response.data;
    },
  });
}

// ==========================================
// F2: Export history hooks for the Reports tab (workspace-scoped).
// ==========================================

export interface ExportJobRow {
  export_id: number;
  destination: string;
  audience_name: string;
  fmt: string;
  record_count: number;
  estimated_audience_size: number;
  meets_min_cohort: boolean;
  requires_partner_activation: boolean;
  filename: string | null;
  status: string;
  created_at: string | null;
}

export function useExports(workspaceKey: string | null, destination?: string,
                           page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ['exports', workspaceKey, destination, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (destination) params.set('destination', destination);
      params.set('limit', String(pageSize));
      params.set('offset', String((page - 1) * pageSize));
      const response = await api.get<{ total: number; limit: number; offset: number; items: ExportJobRow[] }>(
        `/exports?${params}`
      );
      return response.data;
    },
    enabled: !!workspaceKey,
    staleTime: 30 * 1000,
  });
}

/** Auth headers travel via axios, so downloads fetch a blob and save it. */
export function useDownloadExport() {
  return useMutation<void, ApiError, { exportId: number; filename?: string | null }>({
    mutationFn: async ({ exportId, filename }) => {
      const response = await api.get<Blob>(`/exports/${exportId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename ?? `export-${exportId}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });
}
